import { useState, useCallback } from 'react';
import { useETLMonitoring } from '../../hooks/useETLMonitoring';
import { FileDropzone } from '../../components/etl/FileDropzone';
import { DataPreview } from '../../components/etl/DataPreview';
import { EntitySelector } from '../../components/etl/EntitySelector';
import { ETL_SCHEMAS, getSchemaForEntity } from '../../config/etl-schemas';
import { downloadEntityTemplate, getRequiredFieldsSummary } from '../../utils/etl-template';
import * as XLSX from 'xlsx';

export function ETLPage() {
  const [entity, setEntity] = useState<string>('');
  const [data, setData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [validation, setValidation] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const { captureError, captureValidationError, logEvent } = useETLMonitoring(entity);

  const handleFileSelect = useCallback((selectedFile: File) => {
    setFile(selectedFile);
    setData([]);
    setHeaders([]);
    setValidation(null);
    setImportResult(null);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const result = e.target?.result;
        if (!result) {
          setError('Error al leer el archivo');
          return;
        }

        const workbook = XLSX.read(result, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

        // Detectar si es una plantilla oficial (3 primeras filas son cabecera)
        // Leer crudo para inspeccionar
        const rawRows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, blankrows: false }) as any[][];

        let dataRows: any[] = [];
        let headerKeys: string[] = [];

        if (rawRows.length >= 3 && isTemplateHeader(rawRows)) {
          // Es una plantilla: usar la fila 0 como keys, y datos desde fila 3
          headerKeys = rawRows[0].map(h => String(h || '').trim());
          dataRows = rawRows.slice(3).map(row => {
            const obj: Record<string, any> = {};
            headerKeys.forEach((key, idx) => {
              if (key) obj[key] = row[idx];
            });
            return obj;
          });
        } else {
          // Formato tradicional: primera fila es header
          const jsonData = XLSX.utils.sheet_to_json(firstSheet) as Record<string, any>[];
          if (jsonData.length === 0) {
            setError('El archivo está vacío');
            return;
          }
          headerKeys = Object.keys(jsonData[0]);
          dataRows = jsonData;
        }

        // Filtrar filas vacías
        dataRows = dataRows.filter(row =>
          Object.values(row).some(v => v !== undefined && v !== null && v !== '')
        );

        if (dataRows.length === 0) {
          setError('No se encontraron filas con datos válidos. Si usaste la plantilla, asegúrate de haber borrado la fila de ejemplo y llenado tus datos a partir de la fila 4.');
          return;
        }

        setHeaders(headerKeys);
        setData(dataRows);
        logEvent('file_loaded', { fileName: selectedFile.name, rowCount: dataRows.length });

        if (entity) {
          validateData(dataRows, entity);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Error al leer archivo');
        captureError(error, { fileName: selectedFile.name });
        setError(error.message);
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  }, [entity, captureError, logEvent]);

  const validateData = useCallback(async (dataToValidate: any[], entityType: string) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const sessionId = localStorage.getItem('sessionId');
      const ETL_URL = import.meta.env.VITE_API_URL_ETL || 'http://localhost:8788';

      const response = await fetch(`${ETL_URL}/api/etl/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
        },
        body: JSON.stringify({
          data: dataToValidate,
          entity: entityType
        })
      });

      if (!response.ok) {
        // Leer como texto primero para evitar errores de parseo
        const text = await response.text();
        let errorMessage = 'Error al validar datos';
        try {
          const errorData = JSON.parse(text);
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `Error ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      const validationResult = result.data || {};
      setValidation(validationResult);

      if (validationResult.errors && validationResult.errors.length > 0) {
        validationResult.errors.slice(0, 10).forEach((err: any) => {
          captureValidationError(err.row, err.field, err.value, err.message);
        });
      }

      logEvent('validation_complete', {
        entity: entityType,
        total: validationResult.total,
        valid: validationResult.validCount,
        errors: validationResult.errors?.length || 0
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error al validar datos');
      captureError(error, { entity: entityType });
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [captureError, captureValidationError, logEvent]);

  const handleEntityChange = useCallback((newEntity: string) => {
    setEntity(newEntity);
    if (data.length > 0) {
      validateData(data, newEntity);
    }
  }, [data, validateData]);

  const handleImport = useCallback(async () => {
    if (!data.length || !entity) return;

    setIsImporting(true);
    setError(null);
    setImportResult(null);

    const CHUNK_SIZE = 10;
    const totalChunks = Math.ceil(data.length / CHUNK_SIZE);
    const ETL_URL = import.meta.env.VITE_API_URL_ETL || 'http://localhost:8788';

    // Acumuladores de resultados
    let totalImported = 0;
    let totalFailed = 0;
    let totalSkipped = 0;
    const allErrors: any[] = [];
    const summary = {
      clients: 0,
      projects: 0,
      payments: 0,
      expenses: 0,
      skipped: 0,
    };

    try {
      const token = localStorage.getItem('accessToken');
      const sessionId = localStorage.getItem('sessionId');

      for (let chunkIdx = 0; chunkIdx < totalChunks; chunkIdx++) {
        const start = chunkIdx * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, data.length);
        const chunk = data.slice(start, end);

        const response = await fetch(`${ETL_URL}/api/etl/import`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
          },
          body: JSON.stringify({ data: chunk, entity }),
        });

        if (!response.ok) {
          const text = await response.text();
          let errorMessage = `Error al importar chunk ${chunkIdx + 1}/${totalChunks}`;
          try {
            const errorData = JSON.parse(text);
            errorMessage = errorData.error || errorMessage;
          } catch {
            errorMessage = `Error ${response.status}: ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();
        const chunkResult = result.data || {};

        totalImported += chunkResult.imported || 0;
        totalFailed += chunkResult.failed || 0;
        totalSkipped += chunkResult.summary?.skipped || 0;

        if (chunkResult.errors) {
          allErrors.push(...chunkResult.errors.map((e: any) => ({
            row: start + e.row,
            message: e.message,
          })));
        }

        if (chunkResult.summary) {
          summary.clients += chunkResult.summary.clients || 0;
          summary.projects += chunkResult.summary.projects || 0;
          summary.payments += chunkResult.summary.payments || 0;
          summary.expenses += chunkResult.summary.expenses || 0;
          summary.skipped += chunkResult.summary.skipped || 0;
        }
      }

      setImportResult({
        success: allErrors.length === 0,
        imported: totalImported,
        failed: totalFailed,
        errors: allErrors,
        summary,
      });

      if (allErrors.length === 0) {
        setFile(null);
        setData([]);
        setHeaders([]);
        setValidation(null);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error al importar datos');
      captureError(error, { entity });
      setError(error.message);
    } finally {
      setIsImporting(false);
    }
  }, [data, entity, captureError]);

  const handleReset = useCallback(() => {
    setFile(null);
    setData([]);
    setHeaders([]);
    setValidation(null);
    setImportResult(null);
    setError(null);
  }, []);

  const handleDownloadTemplate = useCallback(() => {
    if (!entity) return;
    const schema = getSchemaForEntity(entity);
    if (!schema) return;
    downloadEntityTemplate(schema);
  }, [entity]);

  const currentSchema = entity ? getSchemaForEntity(entity) : null;

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div>
        <h1 className="text-2xl font-bold">Ingesta de Datos (ETL)</h1>
        <p className="text-gray-600">
          Importa datos históricos desde archivos Excel a la plataforma
        </p>
      </div>

      {/* Error global */}
      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          <span>{error}</span>
        </div>
      )}

      {/* Resultado de importación */}
      {importResult && (
        <div className={`info-panel ${importResult.success ? 'success' : 'warning'}`}>
          <span className="info-panel-icon">{importResult.success ? '✅' : '⚠️'}</span>
          <div className="info-panel-body">
            <p className="info-panel-title">
              {importResult.success ? 'Importación completada' : 'Importación con advertencias'}
            </p>
            <p className="info-panel-text">
              {importResult.imported || 0} registros importados, {importResult.failed || 0} fallidos
            </p>
            {importResult.summary && (
              <div style={{ marginTop: '10px', fontSize: '13px' }}>
                {importResult.summary.clients > 0 && (
                  <div>👤 Clientes importados: <strong>{importResult.summary.clients}</strong></div>
                )}
                {importResult.summary.projects > 0 && (
                  <div>📋 Proyectos importados: <strong>{importResult.summary.projects}</strong></div>
                )}
                {importResult.summary.payments > 0 && (
                  <div>💰 Pagos importados: <strong>{importResult.summary.payments}</strong></div>
                )}
                {importResult.summary.expenses > 0 && (
                  <div>💳 Gastos importados: <strong>{importResult.summary.expenses}</strong></div>
                )}
                {importResult.summary.skipped > 0 && (
                  <div style={{ color: '#92400e', marginTop: '4px' }}>
                    ⏭️ Registros omitidos (duplicados): <strong>{importResult.summary.skipped}</strong>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Paso 1: Selección de entidad */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">
            1
          </span>
          <div>
            <h3 className="font-bold">Selecciona el tipo de datos</h3>
            <p className="text-sm text-gray-500">
              Elige qué entidad vas a importar
            </p>
          </div>
        </div>

        <EntitySelector
          value={entity}
          onChange={handleEntityChange}
          disabled={isLoading || isImporting}
        />

        {/* Descripción y campos requeridos de la entidad seleccionada */}
        {currentSchema && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-2xl">{currentSchema.icon}</span>
              <div className="flex-1">
                <p className="font-medium text-blue-900">{currentSchema.name}</p>
                <p className="text-sm text-blue-700">{currentSchema.description}</p>
                <p className="text-xs text-blue-600 mt-2">
                  <strong>Campos obligatorios:</strong> {getRequiredFieldsSummary(currentSchema)}
                </p>
              </div>
              <button
                onClick={handleDownloadTemplate}
                className="btn btn-primary whitespace-nowrap"
                title="Descargar plantilla Excel con el formato esperado"
              >
                📥 Descargar plantilla
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Paso 2: Carga de archivo */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">
            2
          </span>
          <div>
            <h3 className="font-bold">Carga tu archivo Excel</h3>
            <p className="text-sm text-gray-500">
              Formatos soportados: .xlsx, .xls (máximo 50 MB)
            </p>
          </div>
        </div>

        {!file ? (
          <FileDropzone
            onFileSelect={handleFileSelect}
            accept=".xlsx,.xls"
            maxSize={50}
          />
        ) : (
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📄</span>
              <div>
                <p className="font-medium">{file.name}</p>
                <p className="text-sm text-gray-500">
                  {(file.size / 1024).toFixed(2)} KB • {data.length} filas detectadas
                </p>
              </div>
            </div>
            <button
              onClick={handleReset}
              className="btn-action text-red-600"
            >
              🗑️ Cambiar archivo
            </button>
          </div>
        )}

        {/* Nota informativa sobre la plantilla */}
        {!file && currentSchema && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            💡 <strong>Sugerencia:</strong> Si descargaste la plantilla, recuerda <strong>borrar la fila de ejemplo</strong> antes de llenar tus datos.
            Tus registros deben comenzar en la <strong>fila 4</strong>.
          </div>
        )}
      </div>

      {/* Paso 3: Previsualización y validación */}
      {data.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                3
              </span>
              <div>
                <h3 className="font-bold">Previsualización y validación</h3>
                <p className="text-sm text-gray-500">
                  Revisa los datos antes de importarlos
                </p>
              </div>
            </div>
            {validation && (
              <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                validation.valid
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                {validation.valid
                  ? '✅ Datos válidos'
                  : `⚠️ ${validation.errors?.length || 0} errores`}
              </span>
            )}
          </div>

          <DataPreview
            data={data}
            headers={headers}
            validation={validation}
            isLoading={isLoading}
          />

          {/* Errores de validación */}
          {validation && !validation.valid && validation.errors?.length > 0 && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm font-bold text-yellow-800 mb-2">
                ⚠️ Errores de validación detectados:
              </p>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {validation.errors.slice(0, 20).map((err: any, i: number) => (
                  <li key={i} className="text-sm text-yellow-700">
                    <strong>Fila {err.row}:</strong> {err.field} — {err.message}
                  </li>
                ))}
                {validation.errors.length > 20 && (
                  <li className="text-sm text-yellow-700 italic">
                    ... y {validation.errors.length - 20} errores más
                  </li>
                )}
              </ul>
              <p className="text-xs text-yellow-600 mt-2">
                Corrige los errores en tu archivo Excel y vuelve a subirlo.
              </p>
            </div>
          )}

          {/* Botón de importar */}
          {validation && validation.valid && (
            <div className="mt-4">
              <button
                onClick={handleImport}
                disabled={isImporting}
                className="btn btn-primary w-full py-3"
              >
                {isImporting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Importando...
                  </span>
                ) : (
                  `📥 Importar ${data.length} registros`
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Detecta si las primeras filas corresponden a una plantilla oficial.
 * Heurística: la segunda fila contiene etiquetas como "TEXTO", "NÚMERO", "FECHA", "EMAIL", "OPCIONES".
 */
function isTemplateHeader(rows: any[][]): boolean {
  if (rows.length < 3) return false;
  const secondRow = rows[1].map(c => String(c || '').toUpperCase());
  return secondRow.some(cell =>
    cell.includes('TEXTO') ||
    cell.includes('NÚMERO') ||
    cell.includes('NUMERO') ||
    cell.includes('FECHA') ||
    cell.includes('EMAIL') ||
    cell.includes('TELÉFONO') ||
    cell.includes('TELEFONO') ||
    cell.includes('OPCIONES')
  );
}